import { LexicalTypeaheadMenuPlugin } from "@lexical/react/LexicalTypeaheadMenuPlugin";
import { createPortal } from "react-dom";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_NORMAL,
  TextNode,
} from "lexical";
import {
  MutableRefObject,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { $createSuggestionNode } from "./SuggestionNode";
import {
  PluginProps,
  GenericTypeaheadOption,
  Trigger,
  Suggestions,
} from "./type";
import { checkForMentions, getNode, MENTION_REGEXP } from "./helpers";
import { KeyType } from "react-relay/relay-hooks/helpers";
import { debounce } from "lodash";
import { FormattedMessage, useIntl } from "react-intl";
import { supportsPassiveEvents } from "../../../../utils/helpers";
import { Skeleton } from "../../../Skeleton";

export const SUGGESTION_LIST_LENGTH_LIMIT = 10;
const DEBOUCE_DELAY = 300;

interface PortalProps {
  anchorElementRef?: MutableRefObject<HTMLElement | null>;
  activeTrigger?: Trigger | null;
  onClose: () => void;
}
type PortalPropsWithoutActiveTrigger = Omit<PortalProps, "activeTrigger">;

function Portal({
  anchorElementRef,
  onClose,
  activeTrigger,
  children,
}: Readonly<PropsWithChildren<PortalProps>>) {
  useEffect(() => {
    if (!anchorElementRef?.current) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!anchorElementRef.current?.contains(event.target as Node)) {
        onClose();
      }
    };

    window.document.addEventListener(
      "mousedown",
      handleClickOutside,
      supportsPassiveEvents() ? { passive: true } : false,
    );
    return () => {
      window.document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [anchorElementRef, onClose]);

  if (activeTrigger === null || !anchorElementRef?.current) return null;

  return createPortal(
    <div className="w-64 bg-white rounded-md my-2 shadow-md">{children}</div>,
    anchorElementRef.current,
  );
}

function ListItem({ children }: PropsWithChildren) {
  const intl = useIntl();
  return (
    <ul
      role="listbox"
      aria-label={intl.formatMessage({ defaultMessage: "Search Suggestions" })}
      className="flex flex-col gap-2 p-2 max-h-48 overflow-y-scroll"
    >
      {children}
    </ul>
  );
}

function EmptySuggestions(props: PortalPropsWithoutActiveTrigger) {
  return (
    <Portal {...props}>
      <p className="p-2 flex-shrink-0">
        <FormattedMessage defaultMessage="No suggestions available. Keep typing to refine your search!" />
      </p>
    </Portal>
  );
}

function SuggestionSkeleton(props: PortalPropsWithoutActiveTrigger) {
  return (
    <Portal {...props}>
      <Skeleton className="h-14 w-full flex-shrink-0" />
    </Portal>
  );
}

const includesIgnoreCase = (query: string, str?: string) =>
  str?.toLowerCase().includes(query.toLowerCase());

const buildOptions = <TMap extends Record<Trigger, KeyType>>(
  trigger: Trigger | null,
  query: string | null,
  data: Suggestions<TMap>,
  max: number,
) => {
  if (trigger === null || query === null) return [];

  return (data[trigger] || [])
    .filter((edge) => edge?.node)
    .map((edge) => getNode(edge.node))
    .flatMap((node) =>
      includesIgnoreCase(query, node.key) ||
      includesIgnoreCase(query, node.value)
        ? [new GenericTypeaheadOption(node)]
        : [],
    )
    .slice(0, max);
};

const insertSuggestionNode = (node: TextNode) => {
  const selection = $getSelection();
  if ($isRangeSelection(selection)) {
    selection.insertNodes([node]);
  }
};

const moveCursorToEnd = (node: TextNode) => {
  const selection = $getSelection();
  if ($isRangeSelection(selection)) {
    node.selectEnd();
  }
};

interface State<TMap extends Record<Trigger, KeyType>> {
  query: string | null;
  isLoading: boolean;
  data: Suggestions<TMap>;
  trigger: Trigger | null;
}

type Action<TMap extends Record<Trigger, KeyType>> =
  | { kind: "SET_QUERY"; query: string | null }
  | { kind: "SET_LOADING"; isLoading: boolean }
  | { kind: "SET_DATA"; data: Suggestions<TMap> }
  | { kind: "SET_TRIGGER"; trigger: Trigger | null };

function reducer<TMap extends Record<Trigger, KeyType>>(
  state: State<TMap>,
  action: Action<TMap>,
): State<TMap> {
  switch (action.kind) {
    case "SET_QUERY":
      return { ...state, query: action.query };
    case "SET_LOADING":
      return { ...state, isLoading: action.isLoading };
    case "SET_DATA":
      return { ...state, data: action.data };
    case "SET_TRIGGER":
      return { ...state, trigger: action.trigger };
    default:
      return state;
  }
}

export default function Plugin<TMap extends Record<Trigger, KeyType>>({
  onSearch,
  renderItems,
  maxItems = SUGGESTION_LIST_LENGTH_LIMIT,
  delay = DEBOUCE_DELAY,
}: PluginProps<TMap>) {
  const [editor] = useLexicalComposerContext();

  const [state, dispatch] = useReducer(reducer, {
    query: null,
    isLoading: false,
    data: {} as Suggestions<TMap>,
    trigger: null,
  });
  const { query, isLoading, data, trigger } = state;

  const triggerRef = useRef<Trigger | null>(null);

  const setTrigger = useCallback((trigger: Trigger | null) => {
    triggerRef.current = trigger;
    dispatch({ kind: "SET_TRIGGER", trigger });
  }, []);

  const options = useMemo(
    () => buildOptions(trigger, query, data, maxItems),
    [data, trigger, query, maxItems],
  );

  const debouncedOnSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        dispatch({ kind: "SET_LOADING", isLoading: true });
        dispatch({
          kind: "SET_DATA",
          data: await onSearch(query),
        });
        dispatch({ kind: "SET_LOADING", isLoading: false });
      }, delay),
    [onSearch, delay],
  );

  const onClose = useCallback(() => {
    dispatch({ kind: "SET_TRIGGER", trigger: null });
  }, []);

  const handleQueryChange = useCallback(
    async (newQuery: string | null) => {
      if (!triggerRef.current) return;

      dispatch({ kind: "SET_LOADING", isLoading: true });
      const query = newQuery || "";

      if (query === "") {
        debouncedOnSearch.cancel();
        dispatch({ kind: "SET_QUERY", query: "" });
        dispatch({ kind: "SET_DATA", data: await onSearch() });
        dispatch({ kind: "SET_LOADING", isLoading: false });
        return;
      }

      dispatch({ kind: "SET_QUERY", query: newQuery });
      if (newQuery) {
        debouncedOnSearch(newQuery);
      } else {
        debouncedOnSearch.cancel();
        dispatch({ kind: "SET_LOADING", isLoading: false });
      }
    },
    [debouncedOnSearch, onSearch],
  );

  const onSelectOption = useCallback(
    (
      selectedOption: GenericTypeaheadOption<KeyType>,
      nodeToReplace: TextNode | null,
      closeMenu: () => void,
    ): void => {
      const activeTrigger = triggerRef.current;
      if (!activeTrigger) return;

      editor.update(() => {
        const suggestionNode = $createSuggestionNode(
          selectedOption.key,
          activeTrigger,
        );

        nodeToReplace
          ? nodeToReplace.replace(suggestionNode)
          : insertSuggestionNode(suggestionNode);

        moveCursorToEnd(suggestionNode);
        closeMenu();
      });
    },
    [editor],
  );

  const checkForMentionMatch = useCallback(
    (text: string) => {
      const queryMatch = checkForMentions(MENTION_REGEXP, text);
      if (!queryMatch) {
        dispatch({ kind: "SET_TRIGGER", trigger: null });
        return null;
      }

      const { replaceableString, matchingString } = queryMatch;
      const trigger = replaceableString.includes(matchingString)
        ? replaceableString.replace(matchingString, "")
        : replaceableString;

      setTrigger((trigger as Trigger) || null);
      return replaceableString ? queryMatch : null;
    },
    [setTrigger],
  );

  return (
    <LexicalTypeaheadMenuPlugin
      commandPriority={COMMAND_PRIORITY_NORMAL}
      onQueryChange={handleQueryChange}
      onSelectOption={onSelectOption}
      triggerFn={checkForMentionMatch}
      options={options}
      onClose={onClose}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex },
      ) => {
        if (!trigger) return null;
        if (isLoading) {
          return (
            <SuggestionSkeleton
              anchorElementRef={anchorElementRef}
              onClose={onClose}
            />
          );
        }
        if (!isLoading && options.length === 0) {
          return (
            <EmptySuggestions
              anchorElementRef={anchorElementRef}
              onClose={onClose}
            />
          );
        }
        return (
          <Portal
            anchorElementRef={anchorElementRef}
            activeTrigger={trigger}
            onClose={onClose}
          >
            <ListItem>
              {options.map((option, index) =>
                renderItems[trigger](option, {
                  index,
                  isSelected: selectedIndex === index,
                  onClick: () => {
                    setHighlightedIndex(index);
                    selectOptionAndCleanUp(option);
                  },
                  onMouseEnter: () => setHighlightedIndex(index),
                }),
              )}
            </ListItem>
          </Portal>
        );
      }}
    />
  );
}
